import { useEffect } from "preact/hooks";
import { BudgetStatus } from "@kase/my-marathon-communication";
import { useComputed, useSignal } from "@preact/signals";
import { differenceInBusinessDays } from "date-fns";
import { Budget, getBudgetApproval, useApp } from "~/core";
import { useAppTime } from "~/hooks";
import { SingleWidgetProps } from "../interfaces";
import { ApprovalTypeFilter, PendingBudgetView } from "./interfaces";

const APPROVED_VISIBLE_BUSINESS_DAYS = 3;

const isPending = (budget: Budget) =>
    budget.status === BudgetStatus.BudgetStatusInternal ||
    budget.status === BudgetStatus.BudgetStatusExternal;

const isRecentlyApproved = (
    approval: PendingBudgetView["approval"],
    today: Date,
) => {
    if (!approval.external.approved) {
        return true;
    }

    const latest = approval.external.approvals
        .map((a) => a.date)
        .sort((a, b) => b.getTime() - a.getTime())
        .at(0);

    if (!latest) {
        return true;
    }

    return (
        differenceInBusinessDays(today, latest) <=
        APPROVED_VISIBLE_BUSINESS_DAYS
    );
};

const useWidget = ({}: SingleWidgetProps) => {
    const { api } = useApp();
    const today = useAppTime("day");
    const typeFilter = useSignal<ApprovalTypeFilter[]>([
        "internal",
        "external",
    ]);

    const toggleType = (type: ApprovalTypeFilter) => {
        typeFilter.value = typeFilter.value.includes(type)
            ? typeFilter.value.filter((t) => t !== type)
            : [...typeFilter.value, type];
    };

    useEffect(() => {
        // @TODO @TMP: check unsub
        api.budget.listen({
            statuses: [
                BudgetStatus.BudgetStatusInternal,
                BudgetStatus.BudgetStatusExternal,
            ],
        });
        api.supplier.listen();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const candidateBudgets = useComputed(() => {
        const me = api.employee.me.value;

        return api.budget.allBudgets.value
            .filter(isPending)
            .filter((budget) => !!me && !!budget.created_user?.equals(me._id));
    });

    useEffect(() => {
        const cbs = candidateBudgets.value
            .map((budget) => ({
                budget,
                company: api.project.getWithCompany(budget.project)?.company,
            }))
            .filter(
                (item): item is { budget: Budget; company: string } =>
                    !!item.company,
            )
            .map(({ budget, company }) =>
                api.approve.approval.listenBudgets({
                    parent: budget._id,
                    company,
                }),
            );

        return () => cbs.forEach((cb) => cb());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [candidateBudgets.value]);

    const budgets = useComputed(() => {
        return candidateBudgets.value
            .filter(
                (budget) =>
                    (typeFilter.value.includes("internal") &&
                        budget.status === BudgetStatus.BudgetStatusInternal) ||
                    (typeFilter.value.includes("external") &&
                        budget.status === BudgetStatus.BudgetStatusExternal),
            )
            .map(
                (budget): PendingBudgetView => ({
                    budget,
                    project: api.project.getWithCompany(budget.project),
                    approval: getBudgetApproval(
                        budget.id,
                        api.budget.allBudgets.value,
                        api.approve.approval.budgets.value,
                    ),
                }),
            )
            .filter(({ approval }) =>
                isRecentlyApproved(approval, today.value),
            );
    });

    const loading = api.budget.loading({
        statuses: [
            BudgetStatus.BudgetStatusInternal,
            BudgetStatus.BudgetStatusExternal,
        ],
    });

    return { budgets, loading, typeFilter, toggleType };
};

export default useWidget;
